import { walkTree } from '@customerio/dryad';
import type { ConditionNode, GroupNode } from '@customerio/dryad';
import { isRecord } from '@/utils/objects';
import { CONSENT_STATE_CONDITION_TYPE } from '../rows/consent-state-types';

export const ATTRIBUTE_CONDITION_TYPE = 'attribute';

// True for any wire shape an attribute condition can round-trip as: the proto
// filter-tree spelling (an `{ attribute: {...} }` envelope) or the flat
// `type`-tagged legacy-array spelling, either buried under `not` and/or nested
// groups. Used on error-node `raw` payloads (wire dryad couldn't parse into
// editable rows), which may still be attribute conditions.
// fallow-ignore-next-line complexity -- a flat sequence of wire-shape guards; splitting hurts readability
export function isAttributeWire(raw: unknown): boolean {
  if (!isRecord(raw)) {
    return false;
  }
  if ('attribute' in raw || raw.type === ATTRIBUTE_CONDITION_TYPE) {
    return true;
  }
  if ('not' in raw && isAttributeWire(raw.not)) {
    return true;
  }
  const group = raw.and ?? raw.or;
  return Array.isArray(group) && group.some(isAttributeWire);
}

function isAttributeNode(node: ConditionNode): boolean {
  if (node.kind === 'leaf') {
    // Consent state filters on cio_subscription_preferences, a redactable attribute.
    return node.type === ATTRIBUTE_CONDITION_TYPE || node.type === CONSENT_STATE_CONDITION_TYPE;
  }
  if (node.kind === 'error') {
    return isAttributeWire(node.raw);
  }
  return false;
}

function hasAttributeCondition(tree: GroupNode): boolean {
  let found = false;
  walkTree(tree, (node) => {
    if (!isAttributeNode(node)) {
      return undefined;
    }
    found = true;
    return false;
  });
  return found;
}

/**
 * A redacted user (one not permitted to view attribute values — they render as
 * "******") can't touch ANY condition once the filter holds an attribute one
 * anywhere in the tree — including ones dryad kept as error nodes, which may
 * still be attribute conditions. Derive this from the reactive tree and feed
 * the result into the provider's meta: meta is a view concern, derived rather
 * than synced.
 */
export function isAttributeLocked(
  tree: GroupNode,
  options: { canViewAttributes: boolean; disabled: boolean },
): boolean {
  return options.disabled || (!options.canViewAttributes && hasAttributeCondition(tree));
}
